import { useColorMode } from "@/components/layouts/ColorModeContext";
import { CurrentToolUsage, GetToolUsageResponse } from "@/store/services/supabase";
import { PortfolioRegionType, portfolioTypeColours, portfolioTypeLabelColours } from "@/tools/aggregate/portfolio-export/types";
import { translucent } from "@/utils/color";
import { ArrowRightOutlined } from "@ant-design/icons";
import { GlobeSimpleIcon, MapPinIcon } from "@phosphor-icons/react";
import { Button, Card, ConfigProvider, Progress, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { PortfolioTypeSelectionComparison } from "./PortfolioExportTypeSelectionComparison";
const {
  Text,
  Title
} = Typography;
interface PortfolioExportTypeSelectionProps {
  toolUsage: GetToolUsageResponse;
  creatablePortfolioTypes: string[];
  onSelect: (type: PortfolioRegionType) => void;
}
export const PortfolioExportTypeSelection = ({
  toolUsage,
  creatablePortfolioTypes,
  onSelect
}: PortfolioExportTypeSelectionProps) => {
  const hkUsage = toolUsage.singleQuotaMode && toolUsage.singleQuotaUsage ? toolUsage.singleQuotaUsage : toolUsage.portfolioInsights;
  const globalUsage = toolUsage.portfolioInsightsGlobal;
  const usageMap: Record<string, CurrentToolUsage> = {
    hk: hkUsage,
    global: globalUsage
  };
  return <div className="flex flex-col gap-8" data-sentry-component="PortfolioExportTypeSelection" data-sentry-source-file="PortfolioExportTypeSelection.tsx">
      <Text type="secondary" className="text-sm" data-sentry-element="Text" data-sentry-source-file="PortfolioExportTypeSelection.tsx">
        Choose the portfolio framework that matches your analysis scope. Each portfolio type provides different risk
        models, archetypes, and output metrics.
      </Text>
      <div className="flex gap-6">
        {creatablePortfolioTypes.map(type => <PortfolioCard key={type} regionType={type as PortfolioRegionType} onSelect={() => onSelect(type as PortfolioRegionType)} usage={usageMap[type]} />)}
      </div>
      <PortfolioTypeSelectionComparison data-sentry-element="PortfolioTypeSelectionComparison" data-sentry-source-file="PortfolioExportTypeSelection.tsx" />
    </div>;
};
interface PortfolioCardProps {
  onSelect: () => void;
  regionType: PortfolioRegionType;
  usage: CurrentToolUsage;
}
const PortfolioCard = ({
  onSelect,
  regionType,
  usage
}: PortfolioCardProps) => {
  const {
    t
  } = useTranslation("terms", {
    keyPrefix: "portfolioTypes"
  });
  const colour = portfolioTypeColours[regionType];
  const title = t(regionType + ".title") + " Portfolio";
  return <Card className="flex-1 shadow-md" size="small" styles={{
    body: {
      height: "100%"
    }
  }} data-sentry-element="Card" data-sentry-component="PortfolioCard" data-sentry-source-file="PortfolioExportTypeSelection.tsx">
      <ConfigProvider theme={{
      token: {
        colorPrimary: colour
      }
    }} data-sentry-element="ConfigProvider" data-sentry-source-file="PortfolioExportTypeSelection.tsx">
        <Space className="h-full w-full justify-between" orientation="vertical" data-sentry-element="Space" data-sentry-source-file="PortfolioExportTypeSelection.tsx">
          <Space className="w-full" orientation="vertical" size={12} data-sentry-element="Space" data-sentry-source-file="PortfolioExportTypeSelection.tsx">
            <Emblem regionType={regionType} data-sentry-element="Emblem" data-sentry-source-file="PortfolioExportTypeSelection.tsx" />
            <Title level={4} className="!mb-[-.25rem]" data-sentry-element="Title" data-sentry-source-file="PortfolioExportTypeSelection.tsx">
              {title}
            </Title>
            <Text type="secondary" data-sentry-element="Text" data-sentry-source-file="PortfolioExportTypeSelection.tsx">{t(regionType + ".description")}</Text>
          </Space>
          <Space className="w-full" orientation="vertical" size={12} data-sentry-element="Space" data-sentry-source-file="PortfolioExportTypeSelection.tsx">
            <PortfolioQuota regionType={regionType} title={title} usage={usage} data-sentry-element="PortfolioQuota" data-sentry-source-file="PortfolioExportTypeSelection.tsx" />
            <Button block type="primary" onClick={() => onSelect()} disabled={usage.quota === 0 && !usage.alwaysEnabled} data-sentry-element="Button" data-sentry-source-file="PortfolioExportTypeSelection.tsx">
              <Space data-sentry-element="Space" data-sentry-source-file="PortfolioExportTypeSelection.tsx">
                <b>Select {title}</b>
                <ArrowRightOutlined data-sentry-element="ArrowRightOutlined" data-sentry-source-file="PortfolioExportTypeSelection.tsx" />
              </Space>
            </Button>
          </Space>
        </Space>
      </ConfigProvider>
    </Card>;
};
const Emblem = ({
  regionType
}: {
  regionType: PortfolioRegionType;
}) => {
  const {
    colorMode
  } = useColorMode();
  return <div style={{
    // Tinted like the sibling region Tag: the tint composites over the
    // surface, so only the icon needs a per-mode tone.
    backgroundColor: translucent(portfolioTypeColours[regionType], 0.2),
    color: portfolioTypeLabelColours[colorMode][regionType],
    fontSize: "1.5rem"
  }} className="flex max-h-11 min-h-11 min-w-11 max-w-11 items-center justify-center rounded-lg" data-sentry-component="Emblem" data-sentry-source-file="PortfolioExportTypeSelection.tsx">
      {regionType === "hk" ? <MapPinIcon /> : <GlobeSimpleIcon />}
    </div>;
};
interface PortfolioQuotaProps {
  regionType: PortfolioRegionType;
  title: string;
  usage: CurrentToolUsage;
}
const PortfolioQuota = ({
  regionType,
  title,
  usage
}: PortfolioQuotaProps) => {
  const used = usage.usage;
  const maximum = usage.quota;
  const available = Math.max(0, maximum - used);
  if (maximum <= 0) {
    return <div className="text-xs text-gray-500">No quota configured</div>;
  }
  return <Space className="w-full pb-2 pt-3" orientation="vertical" size={0} data-sentry-element="Space" data-sentry-component="PortfolioQuota" data-sentry-source-file="PortfolioExportTypeSelection.tsx">
      <b>{title} Quota</b>
      <Progress strokeColor={portfolioTypeColours[regionType]} percent={used / maximum * 100} showInfo={false} data-sentry-element="Progress" data-sentry-source-file="PortfolioExportTypeSelection.tsx" />
      <Space className="w-full justify-between text-xs" data-sentry-element="Space" data-sentry-source-file="PortfolioExportTypeSelection.tsx">
        <Text type="secondary" data-sentry-element="Text" data-sentry-source-file="PortfolioExportTypeSelection.tsx">{used} used</Text>
        <Text type="secondary" data-sentry-element="Text" data-sentry-source-file="PortfolioExportTypeSelection.tsx">{available} available</Text>
      </Space>
    </Space>;
};